import React from 'react'
import { gql, useMutation, useQuery } from '@apollo/client'
import { toast } from 'react-toastify'

import { useLoading } from '../../context/useLoading'
import CartoonSeries from './CartoonSeries'
import WithMainLayout from '../../hocs/withMainLayout'
import Loading from '../shared/Loading'

const GET_CARTOON_SERIES = gql`
  query statusedCartoonSeries {
    statusedCartoonSeries {
      id
      seriesName
      coverImage
      descriptionKZ
      descriptionRU
      descriptionEN
      year
      country
      status
      index
      seasons {
        id
        title
      }
      seasonsCount
    }
  }
`

const GET_VIDEO_PLAYLISTS = gql`
  query statusedCartoons {
    statusedCartoons {
      id
      title
    }
  }
`

const ADD_CARTOON_SERIES = gql`
  mutation addCartoonSeries(
    $coverImageFile: FileUpload
    $input: CartoonSeriesInput
  ) {
    addCartoonSeries(coverImageFile: $coverImageFile, input: $input) {
      id
      seriesName
      coverImage
      descriptionKZ
      descriptionRU
      descriptionEN
      year
      country
      status
      index
      seasons {
        id
        title
      }
      seasonsCount
    }
  }
`

const UPDATE_CARTOON_SERIES = gql`
  mutation updateCartoonSeries(
    $id: ID!
    $coverImageFile: FileUpload
    $input: UpdateCartoonSeriesInput
  ) {
    updateCartoonSeries(
      id: $id
      coverImageFile: $coverImageFile
      input: $input
    ) {
      id
      seriesName
      coverImage
      descriptionKZ
      descriptionRU
      descriptionEN
      year
      country
      status
      index
      seasons {
        id
        title
      }
      seasonsCount
    }
  }
`

const DELETE_CARTOON_SERIES = gql`
  mutation deleteCartoonSeries($id: ID!) {
    deleteCartoonSeries(id: $id) {
      id
      message
    }
  }
`

const CartoonSeriesContainer = () => {
  const { showLoading, hideLoading } = useLoading()
  const [allCartoonSeries, setAllCartoonSeries] = React.useState([])

  const { data, loading, error, refetch } = useQuery(GET_CARTOON_SERIES)
  const { data: allVideoPlaylists, loading: videoPlaylistsLoading } = useQuery(
    GET_VIDEO_PLAYLISTS
  )

  const [addCartoonSeries, { error: errorAddCartoonSeries }] = useMutation(
    ADD_CARTOON_SERIES,
    {
      update(cache, { data: { addCartoonSeries: addCartoonSeriesItem } }) {
        const { statusedCartoonSeries } = cache.readQuery({
          query: GET_CARTOON_SERIES
        })
        cache.writeQuery({
          query: GET_CARTOON_SERIES,
          data: {
            statusedCartoonSeries: statusedCartoonSeries
              .concat(addCartoonSeriesItem)
              .sort((a, b) =>
                a.index < b.index ? -1 : b.index > a.index ? 1 : 0
              )
          }
        })
        hideLoading()
        toast.success('Серия создана')
      }
    }
  )

  const [
    updateCartoonSeries,
    { error: errorUpdateCartoonSeries }
  ] = useMutation(UPDATE_CARTOON_SERIES, {
    update(cache, { data: { updateCartoonSeries: updateCartoonSeriesItem } }) {
      const { statusedCartoonSeries } = cache.readQuery({
        query: GET_CARTOON_SERIES
      })
      cache.writeQuery({
        query: GET_CARTOON_SERIES,
        data: {
          statusedCartoonSeries: statusedCartoonSeries
            .filter(series => series.id !== updateCartoonSeriesItem.id)
            .concat(updateCartoonSeriesItem)
            .sort((a, b) =>
              a.index < b.index ? -1 : b.index > a.index ? 1 : 0
            )
        }
      })
      hideLoading()
      toast.success('Серия обновлена')
    }
  })

  const [
    deleteCartoonSeries,
    { error: errorDeleteCartoonSeries }
  ] = useMutation(DELETE_CARTOON_SERIES, {
    update(cache, { data: { deleteCartoonSeries: deleteCartoonSeriesItem } }) {
      const { statusedCartoonSeries } = cache.readQuery({
        query: GET_CARTOON_SERIES
      })
      cache.writeQuery({
        query: GET_CARTOON_SERIES,
        data: {
          statusedCartoonSeries: statusedCartoonSeries.filter(
            series => series.id !== deleteCartoonSeriesItem.id
          )
        }
      })
      hideLoading()
      toast.success('Серия удалена')
    }
  })

  React.useEffect(() => {
    if (errorAddCartoonSeries) {
      console.log(errorAddCartoonSeries)
      hideLoading()
      toast.error('Ошибка при создании серии')
    }
  }, [errorAddCartoonSeries])

  React.useEffect(() => {
    if (errorDeleteCartoonSeries) {
      console.log(errorDeleteCartoonSeries)
      hideLoading()
      toast.error('Ошибка при удалении серии')
    }
  }, [errorDeleteCartoonSeries])

  React.useEffect(() => {
    if (errorUpdateCartoonSeries) {
      console.log(errorUpdateCartoonSeries)
      hideLoading()
      toast.error('Ошибка при обновлении серии')
    }
  }, [errorUpdateCartoonSeries])

  React.useEffect(() => {
    if (data && !error && !loading && !videoPlaylistsLoading) {
      setAllCartoonSeries(data.statusedCartoonSeries)
    }
  }, [data, loading, error, videoPlaylistsLoading])

  const addCartoonSeriesHandler = (form, closeFunc, coverImageFile) => {
    form
      .validateFields()
      .then(val => {
        form.resetFields()
        showLoading()
        addCartoonSeries({
          variables: {
            coverImageFile,
            input: {
              seriesName: val.seriesName,
              descriptionKZ: val.descriptionKZ,
              descriptionRU: val.descriptionRU,
              descriptionEN: val.descriptionEN,
              year: val.year,
              country: val.country,
              status: val.status,
              index: val.index,
              seasons: val.seasons || []
            }
          }
        })
      })
      .then(() => {
        closeFunc(false)
      })
      .catch(() => {
        hideLoading()
      })
  }

  const updateCartoonSeriesHandler = (
    form,
    cartoonSeriesId,
    closeFunc,
    coverImageFile
  ) => {
    showLoading()
    form
      .validateFields()
      .then(val => {
        updateCartoonSeries({
          variables: {
            id: cartoonSeriesId,
            coverImageFile,
            input: {
              seriesName: val.seriesName,
              descriptionKZ: val.descriptionKZ,
              descriptionRU: val.descriptionRU,
              descriptionEN: val.descriptionEN,
              year: val.year,
              country: val.country,
              status: val.status,
              index: val.index,
              seasons: val.seasons || []
            }
          }
        })
      })
      .then(() => {
        closeFunc(false)
      })
      .catch(() => {
        hideLoading()
      })
  }

  const deleteCartoonSeriesHandler = (cartoonSeriesId, closeFunc) => {
    showLoading()
    deleteCartoonSeries({
      variables: {
        id: cartoonSeriesId
      }
    }).then(() => {
      closeFunc(false)
    })
  }

  const removeFilesHandler = cartoonSeriesId => {
    updateCartoonSeries({
      variables: {
        id: cartoonSeriesId,
        input: {
          coverImage: null
        }
      }
    })
  }

  if (loading || videoPlaylistsLoading) {
    return <Loading />
  }

  return (
    <CartoonSeries
      cartoonSeries={allCartoonSeries}
      allVideoPlaylists={allVideoPlaylists}
      addCartoonSeriesHandler={addCartoonSeriesHandler}
      updateCartoonSeriesHandler={updateCartoonSeriesHandler}
      deleteCartoonSeriesHandler={deleteCartoonSeriesHandler}
      removeFilesHandler={removeFilesHandler}
    />
  )
}

export default WithMainLayout(CartoonSeriesContainer)
